import { Box, Typography } from '@mui/material';
import ButtonLarge from 'common/ButtonLarge';
import Heading from 'common/Heading';
import PasswordInput from 'common/PasswordInput';
import routes from 'config/routes';
import { useNavigate } from 'react-router-dom';
import supabaseHandler from 'utils/SupaBase';

import { useEffect, useState } from 'react';

const getSupabase = () => {
    
    if (!supabaseHandler) {
        console.error('Supabase supabaseHandler not initialized - check environment variables');
        return null;
    }
    return supabaseHandler;
};

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [canReset, setCanReset] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const access_token = hashParams.get('access_token');
      const refresh_token = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      if (!access_token || !refresh_token || type !== 'recovery') {
        setError('Invalid or expired reset link.');
        return;
      }

      const supabase = getSupabase();
      if (!supabase) {
        setError('Authentication service not available');
        return;
      }
      
      const { error: sessionError } = await supabase.auth.setSession({
        access_token,
        refresh_token,
      });

      if (sessionError) {
        setError('Session setup failed. Please request a new password reset link.');
        return;
      }

      setCanReset(true);
    };

    checkSession();
  }, []);

  const handleResetPassword = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const supabase = getSupabase();
      if (!supabase) {
        setError('Authentication service not available');
        setLoading(false);
        return;
      }
      
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      setMessage('Password has been reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate(routes.auth.login);
      }, 1500);
    } catch (error) {
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'newPassword') {
      setNewPassword(value);
      setPasswordCriteria({
        length: value.length >= 8,
        uppercase: /[A-Z]/.test(value),
        number: /\d/.test(value),
        specialChar: /[.!@#$%^&*()_+\-=]/.test(value),
      });
    }

    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    }
  };

  if (!canReset && !message && !error) {
    return <p className="text-center mt-10">Verifying reset link...</p>;
  }

  return (
    <div className="flex flex-col gap-5 items-center">
      <Heading className="text-center">Reset Password</Heading>

      <PasswordInput
        placeholder="New Password"
        value={newPassword}
        name="newPassword"
        onChange={handleInputChange}
        autoFocus={true}
        onFocus={() => setShowPasswordCriteria(true)}
        isError={!!error}
      />

      {showPasswordCriteria && (
        <Box>
          <h3>Password must include:</h3>
          <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
            <li style={{ color: passwordCriteria.length ? 'green' : 'red' }}>
              <Typography variant="caption">Minimum 8 characters</Typography>
            </li>
            <li style={{ color: passwordCriteria.uppercase ? 'green' : 'red' }}>
              <Typography variant="caption">At least 1 uppercase letter</Typography>
            </li>
            <li style={{ color: passwordCriteria.number ? 'green' : 'red' }}>
              <Typography variant="caption">At least 1 digit</Typography>
            </li>
            <li style={{ color: passwordCriteria.specialChar ? 'green' : 'red' }}>
              <Typography variant="caption">
                At least 1 special character (.!@#$%^&*()_+-=)
              </Typography>
            </li>
          </ul>
        </Box>
      )}

      <PasswordInput
        placeholder="Re-enter New Password"
        value={confirmPassword}
        name="confirmPassword"
        onChange={handleInputChange}
        autoFocus={false}
        isError={!!error}
      />

      <ButtonLarge
        className="w-full"
        loading={loading}
        disabled={
          !canReset ||
          !newPassword ||
          !confirmPassword ||
          !passwordCriteria.length ||
          !passwordCriteria.uppercase ||
          !passwordCriteria.number ||
          !passwordCriteria.specialChar
        }
        onClick={handleResetPassword}
      >
        Reset Password
      </ButtonLarge>

      {error && !message && <p className="text-red-500 text-sm font-medium text-center">{error}</p>}
      {message && <p className="text-green-500 text-sm font-medium text-center">{message}</p>}
    </div>
  );
};

export default ResetPassword;
